// OPG CoPilot session core (CR-20260710-001) — the frontend-owned Conversation aggregate,
// the GLOBAL single-flight Run lock, host identity assignment, the run backstop, and the
// loaded-trade registry (the §9 identity spine for Un-Preview / borders / cleanup).
// Pure logic (no Angular) so the invariants are unit-tested directly. ISOLATED SIBLING:
// re-implements the Hedge/APG design WITHOUT importing it.
//
// ES5 note: arrays only (no Set/Map iteration, no for..of).

import {
    Conversation,
    CopilotLoadedTrade,
    OpgSide,
    OpgTradeSuggestion,
    Run,
    RunContents,
    RunOutcome,
    RunResults,
    SideResult,
    SideTradeContents,
    Tab,
    TabInputs,
    TabKind
} from './opg-copilot-domain';

// ---- Global single-flight lock: at most ONE Run in flight across ALL tabs ----------------

export class SingleFlightLock {
    private _heldBy: string | null = null;

    get isHeld(): boolean {
        return this._heldBy !== null;
    }

    get holder(): string | null {
        return this._heldBy;
    }

    tryAcquire(runId: string): boolean {
        if (this._heldBy !== null) {
            return false;
        }
        this._heldBy = runId;
        return true;
    }

    // Release only if this run holds it (a superseded run must not release a newer holder).
    release(runId: string): void {
        if (this._heldBy === runId) {
            this._heldBy = null;
        }
    }

    releaseAny(): void {
        this._heldBy = null;
    }
}

// ---- Host identity assignment ---------------------------------------------------------------

// Identity = "<runId>#<side>#<index>" for every member. Two same-name trades get DISTINCT
// ids; the name is never a key (identical grid headers are a known hazard — the mock mints
// unique names, but identity never leans on that).
export function buildSideResult(runId: string, side: OpgSide, contents: SideTradeContents): SideResult {
    return {
        members: contents.trades.map((c, i) => ({
            id: runId + '#' + side + '#' + i,
            content: c,
            state: {previewed: false}
        } as OpgTradeSuggestion))
    };
}

export function buildRunResults(runId: string, contents: RunContents): RunResults {
    const results: RunResults = {};
    if (contents.calls) {
        results.calls = buildSideResult(runId, 'calls', contents.calls);
    }
    if (contents.puts) {
        results.puts = buildSideResult(runId, 'puts', contents.puts);
    }
    return results;
}

// ---- Session -------------------------------------------------------------------------------

// Backstop: a Run pending past this budget settles as 'terminalFailed' — stuck is NOT
// empty (§11). Phase 2's real skill inherits this detection unchanged.
export const OPG_COPILOT_RUN_BACKSTOP_MS = 45000;

// Outcomes a skill settlement can carry ('pending'/'cancelled' are FE-only states).
export type SettledOutcome = 'succeeded' | 'empty' | 'terminalFailed' | 'preSendBlocked';

function emptyTab(kind: TabKind): Tab {
    return {kind, inputs: null, currentRun: null, results: null};
}

export class OpgCopilotSession {
    private readonly _lock = new SingleFlightLock();
    private _conversation: Conversation | null = null;
    private _loadedTrades: CopilotLoadedTrade[] = [];
    private _panelInstanceId: string | null = null;

    // idFactory: client-mints Conversation/Run ids (getShortUUID); clock: epoch-ms source.
    // Both injected so the pure specs stay deterministic.
    constructor(
        private readonly _newId: () => string,
        private readonly _now: () => number,
        public readonly backstopMs: number = OPG_COPILOT_RUN_BACKSTOP_MS
    ) {
    }

    // ADR-037/ADR-033 (CR-20260716-001): opaque per-PANEL-INSTANCE id — the backend run
    // registry scopes supersede to (terminalId, panelInstanceId) so two OPG panels on one
    // terminal never supersede each other's runs. The session object lives exactly as long as
    // its panel (the orchestrator creates ONE session per component and never replaces it;
    // conversations restart INSIDE it — startConversation/endConversation), so a
    // session-lifetime id IS a panel-instance id. Minted LAZILY on first read so constructing
    // a session stays allocation-neutral for the deterministic id factories the specs inject.
    get panelInstanceId(): string {
        if (this._panelInstanceId === null) {
            this._panelInstanceId = this._newId();
        }
        return this._panelInstanceId;
    }

    get conversation(): Conversation | null {
        return this._conversation;
    }

    get isRunInFlight(): boolean {
        return this._lock.isHeld;
    }

    get inFlightRunId(): string | null {
        return this._lock.holder;
    }

    // Snapshot copy — callers never mutate the registry directly.
    get loadedTrades(): CopilotLoadedTrade[] {
        return this._loadedTrades.slice();
    }

    startConversation(symbol: string): Conversation {
        this._conversation = {
            id: this._newId(),
            symbol,
            tabs: {calls: emptyTab('calls'), puts: emptyTab('puts'), callsAndPuts: emptyTab('callsAndPuts')}
        };
        this._loadedTrades = [];
        this._lock.releaseAny();
        return this._conversation;
    }

    // Window close / symbol change (§4/§8): the session forgets EVERYTHING — including the
    // registry, because loaded trades deliberately REMAIN in the grid and become the user's
    // own strategies (owner ruling; borders are host state, cleared by the host).
    endConversation(): void {
        this._conversation = null;
        this._loadedTrades = [];
        this._lock.releaseAny();
    }

    tab(kind: TabKind): Tab | null {
        return this._conversation ? this._conversation.tabs[kind] : null;
    }

    // Start a Run on a tab. Returns null if the global lock is held (another Run in flight).
    // Otherwise supersedes this tab's prior CURRENT run, snapshots the inputs, acquires the lock.
    // NOTE: the caller runs resetTabForReRun() FIRST (§7 — re-Run clears that tab's previews).
    startRun(kind: TabKind, inputs: TabInputs): Run | null {
        const tab = this.tab(kind);
        if (!tab) {
            return null;
        }
        const runId = this._newId();
        if (!this._lock.tryAcquire(runId)) {
            return null;   // single-flight: a Run is already pending
        }
        const run: Run = {
            id: runId,
            tabKind: kind,
            sentInputs: inputs,
            outcome: 'pending',
            startedAt: this._now(),
            endedAt: null,
            failureDetail: null,
            callsNote: null,
            putsNote: null
        };
        tab.inputs = inputs;
        tab.currentRun = run;   // supersedes any prior CURRENT run on this tab
        tab.results = null;
        return run;
    }

    // Is this run still the tab's CURRENT one AND the lock holder? Late/backstopped/cancelled
    // results are discarded.
    isCurrent(runId: string): boolean {
        if (this._lock.holder !== runId) {
            return false;
        }
        const tab = this._tabOfRun(runId);
        return tab != null && tab.currentRun != null && tab.currentRun.id === runId;
    }

    // Settle the in-flight Run. Ignores results for a run that is no longer current (late/dup
    // discard). On 'succeeded' builds suggestions with host identities. Releases the global lock.
    completeRun(runId: string, outcome: SettledOutcome, contents: RunContents, failureDetail: string | null): boolean {
        if (!this.isCurrent(runId)) {
            return false;   // discarded
        }
        const tab = this._tabOfRun(runId) as Tab;
        const run = tab.currentRun as Run;
        run.outcome = outcome as RunOutcome;
        run.endedAt = this._now();
        run.failureDetail = failureDetail;
        // CR-20260902-001: empty-side explanations ride the settled run (null when absent).
        run.callsNote = contents.callsNote || null;
        run.putsNote = contents.putsNote || null;
        tab.results = outcome === 'succeeded' ? buildRunResults(runId, contents) : null;
        this._lock.release(runId);
        return true;
    }

    // Cancel the in-flight Run (FE-side). Releases the lock so the user can immediately RUN again.
    cancelRun(runId: string): void {
        const tab = this._tabOfRun(runId);
        if (tab && tab.currentRun && tab.currentRun.id === runId) {
            tab.currentRun.outcome = 'cancelled';
            tab.currentRun.endedAt = this._now();
        }
        this._lock.release(runId);
    }

    // Backstop settlement (§11 stuck ≠ empty): if the run is still the pending CURRENT one,
    // fail it terminally and release the lock. A late skill result after this is discarded
    // by completeRun's isCurrent() check (the lock no longer holds the runId).
    timeoutRun(runId: string): boolean {
        if (!this.isCurrent(runId)) {
            return false;
        }
        const tab = this._tabOfRun(runId) as Tab;
        const run = tab.currentRun as Run;
        if (run.outcome !== 'pending') {
            return false;
        }
        run.outcome = 'terminalFailed';
        run.endedAt = this._now();
        run.failureDetail = 'ORION Took Too Long to Respond. Please Try Again.';
        this._lock.release(runId);
        return true;
    }

    isOverdue(runId: string): boolean {
        if (!this.isCurrent(runId)) {
            return false;
        }
        const tab = this._tabOfRun(runId) as Tab;
        const run = tab.currentRun as Run;
        return run.outcome === 'pending' && (this._now() - run.startedAt) >= this.backstopMs;
    }

    // Re-run reset for a tab (§7: re-Run clears THAT tab's previews first): keeps the
    // questionnaire inputs; clears results + the CURRENT run; removes and RETURNS the tab's
    // loaded-trade rows so the caller unloads the real strategies + borders. Scoped by
    // tabKind (not runId) so previews surviving from a superseded run are cleaned too.
    resetTabForReRun(kind: TabKind): CopilotLoadedTrade[] {
        const tab = this.tab(kind);
        if (!tab) {
            return [];
        }
        tab.results = null;
        tab.currentRun = null;
        const removed = this._loadedTrades.filter(x => x.tabKind === kind);
        this._loadedTrades = this._loadedTrades.filter(x => x.tabKind !== kind);
        return removed;
    }

    // Warned clear-on-run (WI-5): after the host wipes the WHOLE panel (clearAllStrategies),
    // the loaded-trade registry empties and EVERY previewed suggestion across ALL tabs flips
    // back to un-previewed — the panel-wide counterpart to resetTabForReRun (one tab). Only the
    // preview bookkeeping is reset; results + inputs stay (the user still sees the cards, now
    // all un-previewed). Idempotent: the host's per-strategy §8 hook (onHostStrategyRemoved) may
    // have drained some registry rows already; this guarantees the whole set — cards included —
    // in one deterministic pass, so a leftover row can never desync a card's Preview state.
    resetAllPreviews(): void {
        this._loadedTrades = [];
        if (!this._conversation) {
            return;
        }
        const tabs = this._conversation.tabs;
        const all = [tabs.calls, tabs.puts, tabs.callsAndPuts];
        all.forEach(t => {
            if (!t.results) {
                return;
            }
            const sides = [t.results.calls, t.results.puts];
            sides.forEach(side => {
                if (!side) {
                    return;
                }
                side.members.forEach(member => {
                    member.state.previewed = false;
                });
            });
        });
    }

    // ---- Loaded-trade registry (§9 identity: minted strategyId, never name/legs) ----

    registerLoadedTrade(trade: CopilotLoadedTrade): void {
        // A suggestion has at most one loaded instance — replace, never duplicate.
        this._loadedTrades = this._loadedTrades.filter(x => x.suggestionId !== trade.suggestionId);
        this._loadedTrades.push(trade);
    }

    unregisterByStrategyId(strategyId: string): CopilotLoadedTrade | null {
        return this._removeFirst(x => x.strategyId === strategyId);
    }

    unregisterBySuggestionId(suggestionId: string): CopilotLoadedTrade | null {
        return this._removeFirst(x => x.suggestionId === suggestionId);
    }

    findLoadedBySuggestionId(suggestionId: string): CopilotLoadedTrade | null {
        const ix = this._indexOf(x => x.suggestionId === suggestionId);
        return ix === -1 ? null : this._loadedTrades[ix];
    }

    // Locate a suggestion (any result member) across all tabs — the consistency-rule
    // entry point (§8: host removal flips the card's previewed state).
    findSuggestion(suggestionId: string): OpgTradeSuggestion | null {
        if (!this._conversation) {
            return null;
        }
        const tabs = this._conversation.tabs;
        const all = [tabs.calls, tabs.puts, tabs.callsAndPuts];
        let match: OpgTradeSuggestion | null = null;
        all.forEach(t => {
            if (!t.results) {
                return;
            }
            const sides = [t.results.calls, t.results.puts];
            sides.forEach(side => {
                if (!side) {
                    return;
                }
                side.members.forEach(member => {
                    if (member.id === suggestionId) {
                        match = member;
                    }
                });
            });
        });
        return match;
    }

    // WI-6 (cross-date preview navigation): the loaded trades bound to an expiration OTHER than
    // `dateKey`. The orchestrator auto-un-previews exactly this set before switching the panel to
    // dateKey, so a loaded column is never stranded at a foreign date. Snapshot copy (filter) — the
    // caller unloads while iterating, which must not disturb the walk.
    loadedTradesOnOtherDates(dateKey: string): CopilotLoadedTrade[] {
        return this._loadedTrades.filter(x => x.expiration !== dateKey);
    }

    private _removeFirst(predicate: (t: CopilotLoadedTrade) => boolean): CopilotLoadedTrade | null {
        const ix = this._indexOf(predicate);
        if (ix === -1) {
            return null;
        }
        const removed = this._loadedTrades[ix];
        this._loadedTrades.splice(ix, 1);
        return removed;
    }

    private _indexOf(predicate: (t: CopilotLoadedTrade) => boolean): number {
        for (let i = 0; i < this._loadedTrades.length; i++) {
            if (predicate(this._loadedTrades[i])) {
                return i;
            }
        }
        return -1;
    }

    private _tabOfRun(runId: string): Tab | null {
        if (!this._conversation) {
            return null;
        }
        const tabs = this._conversation.tabs;
        const all = [tabs.calls, tabs.puts, tabs.callsAndPuts];
        let match: Tab | null = null;
        all.forEach(t => {
            if (t.currentRun && t.currentRun.id === runId) {
                match = t;
            }
        });
        return match;
    }
}
